import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * Event fired on `document` whenever the wishlist changes (add/remove/clear/sync),
 * so every <wishlist-button> and <wishlist-count-badge> on the page — product
 * cards, the PDP, the header icon — stays in sync without re-rendering anything.
 */
export class WishlistUpdateEvent extends Event {
  static eventName = 'wishlist:update';

  /**
   * @param {string} productId
   * @param {boolean} inWishlist
   */
  constructor(productId, inWishlist) {
    super(WishlistUpdateEvent.eventName, { bubbles: true });
    this.detail = { productId, inWishlist };
  }
}

/**
 * Local-first wishlist storage. Works fully offline (guest shoppers, or the
 * sync backend being unreachable) via localStorage, and opportunistically
 * mirrors to app.elirian.com for signed-in shoppers so the list follows them
 * across devices. A failed/skipped sync never blocks the local experience —
 * localStorage is always the source of truth for what renders on this device.
 */
export class WishlistStore {
  static #STORAGE_KEY = 'elirian:wishlist';
  static #MAX_ITEMS = 500;

  /** @type {string | null} */
  static #syncBase = null;

  /** Call once (from the theme layout) to enable cross-device sync. @param {string} base */
  static configureSync(base) {
    this.#syncBase = base.replace(/\/$/, '');
  }

  static #customerId() {
    return (/** @type {any} */ (window).ELIRIAN_CONFIG?.customerId ?? null) || null;
  }

  static #customerEmail() {
    return (/** @type {any} */ (window).ELIRIAN_CONFIG?.customerEmail ?? null) || null;
  }

  /**
   * @returns {{productId: string, variantId: string|null, handle: string, addedAt: number}[]}
   */
  static getItems() {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY);
      const items = raw ? JSON.parse(raw) : [];
      return Array.isArray(items) ? items : [];
    } catch {
      return [];
    }
  }

  /** @param {string} productId */
  static has(productId) {
    return this.getItems().some((item) => item.productId === String(productId));
  }

  static count() {
    return this.getItems().length;
  }

  /**
   * @param {{productId: string|number, variantId?: string|number|null, handle: string}} item
   */
  static add(item) {
    const productId = String(item.productId);
    const items = this.getItems().filter((existing) => existing.productId !== productId);

    items.unshift({
      productId,
      variantId: item.variantId != null ? String(item.variantId) : null,
      handle: item.handle,
      addedAt: Date.now(),
    });

    this.#persist(items.slice(0, this.#MAX_ITEMS));
    this.#announce(productId, true);
    this.#scheduleSync();
  }

  /** @param {string|number} productId */
  static remove(productId) {
    const id = String(productId);
    const items = this.getItems().filter((existing) => existing.productId !== id);
    this.#persist(items);
    this.#announce(id, false);
    this.#scheduleSync();
  }

  /**
   * @param {{productId: string|number, variantId?: string|number|null, handle: string}} item
   * @returns {boolean} the new "in wishlist" state
   */
  static toggle(item) {
    const inWishlist = this.has(String(item.productId));
    if (inWishlist) {
      this.remove(item.productId);
    } else {
      this.add(item);
    }
    return !inWishlist;
  }

  static clear() {
    this.#persist([]);
    document.dispatchEvent(new WishlistUpdateEvent('*', false));
    this.#scheduleSync();
  }

  /** @param {{productId: string, variantId: string|null, handle: string, addedAt: number}[]} items */
  static #persist(items) {
    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Storage full/disabled (private browsing) — the toggle still worked for
      // this page view via the in-memory event, it just won't persist.
    }
  }

  /**
   * @param {string} productId
   * @param {boolean} inWishlist
   */
  static #announce(productId, inWishlist) {
    document.dispatchEvent(new WishlistUpdateEvent(productId, inWishlist));
  }

  // ── Cross-device sync (optional enhancement; never blocks the local UI) ────

  static #scheduleSync = debounce(() => {
    WishlistStore.#syncToServer();
  }, 800);

  static async #syncToServer() {
    const customerId = this.#customerId();
    if (!this.#syncBase || !customerId) return;

    try {
      await fetch(`${this.#syncBase}/api/v1/wishlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopify_customer_id: customerId,
          customer_email: this.#customerEmail(),
          items: this.getItems(),
        }),
      });
    } catch {
      // Offline, or the app is down — localStorage already has the real list
      // for this device. The next successful sync reconciles it.
    }
  }

  /**
   * Pulls the account's server-synced wishlist (if any) and merges it into this
   * device's local list, so a shopper who saved items elsewhere sees them here
   * too. Call once per page load when a customer is signed in. Additive only —
   * never removes local items the server doesn't know about yet.
   */
  static async pullAndMerge() {
    const customerId = this.#customerId();
    if (!this.#syncBase || !customerId) return;

    try {
      const response = await fetch(
        `${this.#syncBase}/api/v1/wishlist?shopify_customer_id=${encodeURIComponent(customerId)}`
      );
      if (!response.ok) return;

      const data = await response.json();
      const remoteItems = Array.isArray(data.items) ? data.items : [];
      if (!remoteItems.length) return;

      const localItems = this.getItems();
      const localIds = new Set(localItems.map((item) => item.productId));
      const merged = [...localItems];

      for (const remote of remoteItems) {
        const productId = String(remote.productId ?? remote.product_id ?? '');
        if (!productId || localIds.has(productId)) continue;
        merged.push({
          productId,
          variantId: remote.variantId != null ? String(remote.variantId) : (remote.variant_id ?? null),
          handle: remote.handle ?? '',
          addedAt: remote.addedAt ?? Date.now(),
        });
      }

      if (merged.length !== localItems.length) {
        this.#persist(merged.slice(0, this.#MAX_ITEMS));
        document.dispatchEvent(new WishlistUpdateEvent('*', true));
      }
    } catch {
      // No server copy reachable — local list stands as-is.
    }
  }
}

/**
 * The heart-shaped toggle button. Used identically on product cards and the
 * product page; only the surrounding markup differs.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button
 * @extends {Component<Refs>}
 */
export class WishlistButton extends Component {
  requiredRefs = ['button'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.refs.button.addEventListener('click', this.#handleClick, { signal });
    document.addEventListener(WishlistUpdateEvent.eventName, this.#handleExternalUpdate, { signal });

    this.#render(WishlistStore.has(this.#productId));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  get #productId() {
    return this.getAttribute('data-product-id') ?? '';
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const inWishlist = WishlistStore.toggle({
      productId: this.#productId,
      variantId: this.getAttribute('data-variant-id') || null,
      handle: this.getAttribute('data-product-handle') ?? '',
    });

    this.#render(inWishlist);
  };

  /** @param {WishlistUpdateEvent} event */
  #handleExternalUpdate = (event) => {
    const { productId, inWishlist } = event.detail;
    if (productId === this.#productId) {
      this.#render(inWishlist);
    } else if (productId === '*') {
      this.#render(WishlistStore.has(this.#productId));
    }
  };

  /** @param {boolean} inWishlist */
  #render(inWishlist) {
    this.toggleAttribute('data-in-wishlist', inWishlist);
    this.refs.button.setAttribute('aria-pressed', String(inWishlist));
    this.refs.button.setAttribute(
      'aria-label',
      inWishlist ? (this.dataset.removeLabel ?? '') : (this.dataset.addLabel ?? '')
    );
  }
}

/**
 * The small count bubble shown on the header wishlist link. Mirrors
 * cart-icon.js's cart-bubble pattern, just driven by WishlistStore instead
 * of a cart event.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bubble
 * @property {HTMLElement} count
 * @extends {Component<Refs>}
 */
export class WishlistCountBadge extends Component {
  requiredRefs = ['bubble', 'count'];

  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    document.addEventListener(WishlistUpdateEvent.eventName, this.#handleUpdate, { signal: this.#controller.signal });

    this.#render(WishlistStore.count());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  #handleUpdate = () => {
    this.#render(WishlistStore.count());
  };

  /** @param {number} count */
  #render(count) {
    this.refs.bubble.classList.toggle('visually-hidden', count === 0);
    this.refs.count.textContent = count > 0 ? (count < 100 ? String(count) : '99+') : '';
    this.classList.toggle('header-actions__wishlist-icon--has-items', count > 0);
  }
}

const CLOSE_ICON_SVG =
  '<svg aria-hidden="true" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M11.1765 12.025C11.4108 12.2594 11.7907 12.2594 12.025 12.025C12.2594 11.7907 12.2594 11.4108 12.025 11.1765L8.84931 8.00078L12.025 4.82505C12.2594 4.59073 12.2594 4.21083 12.025 3.97652C11.7907 3.7422 11.4108 3.7422 11.1765 3.97652L8.00078 7.15225L4.82505 3.97652C4.59073 3.7422 4.21083 3.7422 3.97652 3.97652C3.7422 4.21083 3.7422 4.59073 3.97652 4.82505L7.15225 8.00078L3.97652 11.1765C3.7422 11.4108 3.7422 11.7907 3.97652 12.025C4.21083 12.2594 4.59073 12.2594 4.82505 12.025L8.00078 8.84931L11.1765 12.025Z" fill="currentColor"/>' +
  '</svg>';

/**
 * Formats a price in cents using the shop's money_format string (the same
 * `{{amount}}`-style placeholder Shopify uses in Liquid's `money` filter).
 * Mirrors the inline implementation in recently-viewed.liquid.
 *
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';

  function withDelimiters(number, precision, thousands, decimal) {
    if (isNaN(number) || number == null) return '0';
    const fixed = (number / 100).toFixed(precision);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
    const decimals = parts[1] ? `${decimal}${parts[1]}` : '';
    return dollars + decimals;
  }

  const match = formatString.match(placeholderRegex);
  let value = '';
  switch (match?.[1]) {
    case 'amount':
      value = withDelimiters(cents, 2, ',', '.');
      break;
    case 'amount_no_decimals':
      value = withDelimiters(cents, 0, ',', '.');
      break;
    case 'amount_with_comma_separator':
      value = withDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = withDelimiters(cents, 0, '.', ',');
      break;
    default:
      value = withDelimiters(cents, 2, ',', '.');
  }

  return formatString.replace(placeholderRegex, value);
}

/** @param {string} value */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

/**
 * The wishlist drawer's contents: a short list of saved items with a link to
 * the full wishlist page. Rendered once globally (layout/theme.liquid); the
 * header heart icon just toggles the shared <theme-drawer> open by id.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} empty
 * @property {HTMLElement} list
 * @property {HTMLElement} footer
 * @extends {Component<Refs>}
 */
export class WishlistDrawerComponent extends Component {
  requiredRefs = ['empty', 'list', 'footer', 'announcer'];

  static #MAX_VISIBLE = 8;

  // Matches the app domain already hardcoded for the other storefront
  // widgets (Design Studio, Loyalty) in layout/theme.liquid, and for the
  // wishlist page's own share flow — kept consistent rather than
  // introducing a separate settings field for the same URL.
  static #SYNC_BASE = 'https://app.elirian.com';

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {boolean} */
  #hasRendered = false;

  /** @type {string | null} */
  #resolvedShareUrl = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;

    document.addEventListener('theme-drawer:open', this.#handleDrawerOpen, { signal });
    document.addEventListener(WishlistUpdateEvent.eventName, this.#handleWishlistUpdate, { signal });
    this.querySelector('[data-share-network="copy"]')?.addEventListener('click', this.#handleCopyClick, { signal });

    // Drawers can be restored already-open after a full page navigation (see
    // theme-drawer.liquid's synchronous restore script), which sets [open]
    // directly without dispatching 'theme-drawer:open'. Catch that path here,
    // otherwise the list stays whatever it was on the previous page (or
    // blank, on first connect) until the shopper manually closes/reopens it.
    if (this.closest('theme-drawer')?.hasAttribute('open')) {
      this.#renderList();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
  }

  /**
   * Issues (or reuses) a share token for the current list, same as the
   * wishlist page's own share flow. Sent regardless of sign-in state — the
   * backend creates an anonymous share record when the shopper isn't signed
   * into a Shopify customer account.
   */
  async #resolveShareUrl() {
    if (this.#resolvedShareUrl) return this.#resolvedShareUrl;

    let shareUrl = `${window.location.origin}${this.dataset.wishlistUrl}`;

    try {
      const response = await fetch(`${WishlistDrawerComponent.#SYNC_BASE}/api/v1/wishlist/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shopify_customer_id: /** @type {any} */ (window).ELIRIAN_CONFIG?.customerId ?? null,
          items: WishlistStore.getItems(),
        }),
      });
      if (response.ok) {
        const data = await response.json();
        if (data.share_token) {
          shareUrl = `${shareUrl}?share=${data.share_token}`;
        }
      }
    } catch {
      // Fall back to sharing the plain page URL below.
    }

    this.#resolvedShareUrl = shareUrl;
    return shareUrl;
  }

  async #populateShareLinks() {
    // Signed-out shoppers get a "Sign in to share" link instead of the
    // share icon row (guest shares aren't exposed in the UI even though
    // the backend can technically support them) — nothing to populate.
    if (!this.querySelector('[data-wishlist-drawer-share]')) return;

    const url = await this.#resolveShareUrl();
    if (!this.isConnected) return;

    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(this.dataset.shareTitle ?? '');

    const targets = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      pinterest: `https://pinterest.com/pin/create/link/?url=${encodedUrl}&description=${encodedTitle}`,
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodedUrl}`,
    };

    for (const [network, href] of Object.entries(targets)) {
      const link = this.querySelector(`[data-share-network="${network}"]`);
      if (link) link.setAttribute('href', href);
    }
  }

  #handleCopyClick = async () => {
    try {
      const url = await this.#resolveShareUrl();
      await navigator.clipboard.writeText(url);
      this.refs.announcer.textContent = this.dataset.shareCopiedLabel ?? '';
    } catch {
      // Clipboard API unavailable — nothing more we can do silently.
    }
  };

  /** @param {CustomEvent} event */
  #handleDrawerOpen = (event) => {
    if (/** @type {HTMLElement} */ (event.target)?.id !== 'wishlist-drawer') return;
    this.#renderList();
  };

  #handleWishlistUpdate = () => {
    // Only re-fetch while the drawer is actually open — avoids unnecessary
    // network calls every time a heart icon is toggled elsewhere on the page.
    if (this.closest('theme-drawer')?.hasAttribute('open')) {
      this.#renderList();
    } else {
      this.#hasRendered = false;
    }
  };

  async #renderList() {
    this.#hasRendered = true;
    const items = WishlistStore.getItems().slice(0, WishlistDrawerComponent.#MAX_VISIBLE);

    if (!items.length) {
      this.refs.list.innerHTML = '';
      this.refs.list.hidden = true;
      this.refs.footer.hidden = true;
      this.refs.empty.hidden = false;
      return;
    }

    const products = await Promise.all(
      items.map((item) =>
        fetch(`/products/${item.handle}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );
    const valid = products.filter(Boolean);

    if (!this.isConnected) return;

    if (!valid.length) {
      this.refs.list.innerHTML = '';
      this.refs.list.hidden = true;
      this.refs.footer.hidden = true;
      this.refs.empty.hidden = false;
      return;
    }

    this.refs.empty.hidden = true;
    this.refs.footer.hidden = false;
    this.refs.list.hidden = false;
    this.refs.list.innerHTML = '';
    this.#populateShareLinks();

    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const removeLabel = this.dataset.removeLabel || '';

    for (const product of valid) {
      const image = product.featured_image || product.images?.[0] || null;
      const li = document.createElement('li');
      li.className = 'wishlist-drawer__item';
      li.innerHTML = `
        <a href="/products/${product.handle}" class="wishlist-drawer__item-media">
          ${image ? `<img src="${image}" alt="" loading="lazy">` : ''}
        </a>
        <a href="/products/${product.handle}" class="wishlist-drawer__item-info">
          <span class="wishlist-drawer__item-title">${escapeHtml(product.title)}</span>
          <span class="wishlist-drawer__item-price">${formatMoney(product.price, moneyFormat)}</span>
        </a>
        <button type="button" class="button-unstyled wishlist-drawer__item-remove" aria-label="${escapeHtml(removeLabel)}">
          ${CLOSE_ICON_SVG}
        </button>
      `;

      li.querySelectorAll('a').forEach((link) => {
        link.addEventListener('click', () => {
          this.closest('theme-drawer')?.removeAttribute('open');
        });
      });
      li.querySelector('.wishlist-drawer__item-remove')?.addEventListener('click', () => {
        WishlistStore.remove(product.id);
      });

      this.refs.list.appendChild(li);
    }
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
if (!customElements.get('wishlist-count-badge')) {
  customElements.define('wishlist-count-badge', WishlistCountBadge);
}
if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
